import React from 'react';
import { RadioButtonGroup } from '@roger/ui-components';
import { useBespokeSectionContext } from './BespokeSectionContext';
import { useQuestionFieldDisabled } from './useQuestionFieldDisabled';
import { QidTag } from '../../../dev/QidTag';
import {
  joinClasses,
  BESPOKE_FIELD_LABEL_CLASS,
  BESPOKE_FIELD_STACK_GAP_CLASS,
  BESPOKE_RADIO_ITEM_CLASS,
  BESPOKE_OPTION_LABEL_CLASS,
  BESPOKE_OPTION_LABEL_DISABLED_CLASS,
  BESPOKE_CONTROL_ROW_CLASS,
  BESPOKE_EMPTY_STATE_CLASS,
  BESPOKE_EMPTY_STATE_CODE_CLASS,
} from './fieldClassSlots';
import { resolveControlClassName } from './resolveFieldClassNames';

export interface OASISRadioRenderProps {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  label: string;
}

export interface OASISRadioProps {
  qid: string;
  /** Display-only replacement for the schema prompt; answer binding still uses qid. */
  labelOverride?: string;
  /** Outer `[data-qid]` wrapper — grid spans, margins */
  className?: string;
  style?: React.CSSProperties;
  /**
   * Inner wrapper around prompt + radio cluster. Omit for defaults (`space-y-1.5` or inline flex strip).
   * When set, replaces those defaults entirely.
   */
  innerClassName?: string;
  /** Classes appended to prompt label/spans */
  labelClassName?: string;
  /** RadioButtonGroup root — row/column spacing, wrapping (`gap-*`, `flex-wrap`, …) */
  groupClassName?: string;
  /** Per-option row (item + label) */
  optionRowClassName?: string;
  /** Per-option label beside each radio */
  optionLabelClassName?: string;
  controlClassName?: string;
  /** @deprecated Prefer `controlClassName`. */
  itemClassName?: string;
  /** Pass-through to RadioButtonGroup (`horizontal` lays options left-to-right) */
  orientation?: 'horizontal' | 'vertical';
  hideLabel?: boolean;
  /**
   * Prompt and option cluster on one row (within `innerClassName` or default strip).
   * Useful with `orientation: "horizontal"` for compact Yes / No strips.
   */
  inline?: boolean;
  children?: (props: OASISRadioRenderProps) => React.ReactNode;
}

export function OASISRadio({
  qid,
  labelOverride,
  className,
  style,
  innerClassName,
  labelClassName,
  groupClassName,
  optionRowClassName,
  optionLabelClassName,
  controlClassName,
  itemClassName,
  orientation,
  hideLabel,
  inline,
  children,
}: OASISRadioProps) {
  const { answers, onAnswerChange, schemaMap } = useBespokeSectionContext();
  const question = schemaMap.get(qid);
  const inheritedDisabled = useQuestionFieldDisabled(question);

  const value = (answers[qid] as string) ?? '';
  const onChange = (v: string) => onAnswerChange(qid, v);

  const options = (question?.values ?? []).map(val => ({
    value: val,
    label: question?.value_description?.[val]
      ? `${val} - ${question.value_description[val]}`
      : val,
  }));

  const label =
    labelOverride ?? question?.question ?? question?.description ?? qid;

  const innerDefault = inline
    ? joinClasses('flex min-w-0 flex-col', BESPOKE_FIELD_STACK_GAP_CLASS)
    : BESPOKE_FIELD_STACK_GAP_CLASS;
  const inner = innerClassName != null ? innerClassName : innerDefault;

  const blockLabelCls = joinClasses(
    BESPOKE_FIELD_LABEL_CLASS,
    'break-words',
    labelClassName
  );
  const resolvedItemClassName = joinClasses(
    BESPOKE_RADIO_ITEM_CLASS,
    resolveControlClassName(controlClassName, itemClassName)
  );
  const resolvedOptionLabelClassName = joinClasses(
    BESPOKE_OPTION_LABEL_CLASS,
    inheritedDisabled && BESPOKE_OPTION_LABEL_DISABLED_CLASS,
    optionLabelClassName
  );
  const resolvedOptionRowClassName = joinClasses(
    BESPOKE_CONTROL_ROW_CLASS,
    optionRowClassName
  );

  const rg =
    options.length > 0 ? (
      <RadioButtonGroup
        disabled={inheritedDisabled}
        {...(orientation != null ? { orientation } : {})}
        className={groupClassName}
        options={options}
        value={value}
        onValueChange={onChange}
        allowDeselect
        itemClassName={resolvedItemClassName}
        optionLabelClassName={resolvedOptionLabelClassName}
        optionRowClassName={resolvedOptionRowClassName}
      />
    ) : (
      <p className={BESPOKE_EMPTY_STATE_CLASS}>
        No choices in loaded schema for{' '}
        <code className={BESPOKE_EMPTY_STATE_CODE_CLASS}>{qid}</code>. Select a
        RogerDoc schema + section that define this radio in the slice.
      </p>
    );

  return (
    <div
      data-qid={qid}
      className={joinClasses('min-w-0', className)}
      style={style}
    >
      <QidTag qid={qid} />
      {children ? (
        children({ value, onChange, options, label })
      ) : inline ? (
        <div className={inner}>
          {!hideLabel && <label className={blockLabelCls}>{label}</label>}
          <div className='min-w-0'>{rg}</div>
        </div>
      ) : (
        <div className={inner}>
          {!hideLabel && <label className={blockLabelCls}>{label}</label>}
          {rg}
        </div>
      )}
    </div>
  );
}
